import React from 'react';
import type {ColumnType} from 'antd/es/table';
import type {FilterDropdownProps} from 'antd/es/table/interface';
import {Input, Button, Space, TableProps, message, Empty, Grid} from 'antd';
import {SearchOutlined, FilterFilled} from '@ant-design/icons';
import type {Dispatch} from 'redux';
import type {NavigateFunction} from 'react-router';
import authService from '../services/authService';
import {removeCookie} from 'typescript-cookie';
import * as authActions from '../store/modules/auth/actions';
import {resetAnalyticsUser} from '../analytics/posthog';

export interface ListUserFilters {
    userType: string;
    search: string;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
}

export interface ListCampaignFilters {
    status: string;
    search: string;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
}

export interface ListCampaignLinkFilters {
    isActive?: boolean;
    search: string;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
}

// Generic helpers

export function handleLogout(dispatch: Dispatch, navigate: NavigateFunction) {
    return async () => {
        try {
            await authService.logout();
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Logout failed.');
        } finally {
            removeCookie('token', {path: '/'});
            removeCookie('roleType', {path: '/'});
            resetAnalyticsUser();
            dispatch(authActions.logoutSuccess());
            navigate('/');
        }
    };
}

export const openSignup = (roleType?: unknown, signupSource?: string) => {
    window.dispatchEvent(new CustomEvent('open-signup', {
        detail: {
            roleType: typeof roleType === 'string' ? roleType : undefined,
            signupSource
        }
    }));
};

export function formatString(s: string): string {
    if (!s) return '';
    const normalized = s.toLowerCase().replace(/_/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export const formatValue = (value?: number) => {
    if (value === undefined || value === null) return '0.00';
    return Number(value).toFixed(2);
};

export const formatLabel = (s: string) =>
    s.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/_/g, ' ').trim();

// Table helpers

export function formatCurrency(amount: number | null | undefined, symbol = '$', decimals = 2): string {
    if (amount == null || isNaN(amount)) return '-';
    return `${symbol}${amount.toFixed(decimals)}`;
}

export function getSearchFilterProps<RecordType = any>(
    dataIndex: string,
    placeholder = 'Search…'
): Pick<ColumnType<RecordType>, 'filterDropdown' | 'filterIcon' | 'onFilter'> {
    return {
        filterDropdown: (props: FilterDropdownProps) => (
            <div style={{padding: 8}}>
                <Input
                    placeholder={placeholder}
                    value={props.selectedKeys[0] as string}
                    onChange={e =>
                        props.setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => props.confirm()}
                    style={{width: 188, marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button type="primary" onClick={() => props.confirm()} size="small" style={{width: 90}}>
                        Search
                    </Button>
                    <Button onClick={() => {
                        props.clearFilters?.();
                        props.confirm();
                    }}
                            size="small"
                            style={{width: 90}}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}}/>
        ),

        onFilter: (value, record) => {
            const parts = dataIndex.split('.');
            let v: any = record;
            for (const p of parts) {
                v = v?.[p];
                if (v == null) break;
            }
            return (v?.toString().toLowerCase().includes((value as string).toLowerCase()) ?? false);
        },
    };
}

export const discreteFilterIcon = (filtered: boolean) => (
    <FilterFilled style={{color: filtered ? '#1890ff' : undefined}}/>
);

// Feed - Best Advertiser/Promoter/Partner pages

export function createUserTableChangeHandler<T = any>(
    filters: ListUserFilters,
    setFilters: React.Dispatch<React.SetStateAction<ListUserFilters>>): TableProps<T>['onChange'] {
    return (_pagination, tableFilters, sorter) => {
        const userTypeArr = (tableFilters.userType as string[]) || [];
        const searchArr = (tableFilters.search as string[]) || [];

        let sortBy = filters.sortBy;
        let sortDirection = filters.sortDirection;
        if (sorter && 'field' in sorter && sorter.field && sorter.order) {
            sortBy = sorter.field as string;
            sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        setFilters({
            userType: userTypeArr.length ? userTypeArr[0] : '',
            search: searchArr.length ? searchArr[0] : '',
            sortBy,
            sortDirection,
        });
    };
}

export function createCampaignTableChangeHandler<T = any>(
    filters: ListCampaignFilters,
    setFilters: React.Dispatch<React.SetStateAction<ListCampaignFilters>>): TableProps<T>['onChange'] {
    return (_pagination, tableFilters, sorter) => {
        const statusArr = (tableFilters.status as string[]) || [];
        const searchArr = (tableFilters.search as string[]) || [];

        let sortBy = filters.sortBy;
        let sortDirection = filters.sortDirection;
        if (sorter && 'field' in sorter && sorter.field && sorter.order) {
            sortBy = sorter.columnKey as string;
            sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        setFilters({
            status: statusArr.length ? statusArr[0] : '',
            search: searchArr.length ? searchArr[0] : '',
            sortBy,
            sortDirection,
        });
    };
}

export function createCampaignLinkTableChangeHandler<T = any>(
    filters: ListCampaignLinkFilters,
    setFilters: React.Dispatch<React.SetStateAction<ListCampaignLinkFilters>>): TableProps<T>['onChange'] {
    return (_pagination, tableFilters, sorter) => {
        const statusArr = (tableFilters.status as string[]) || [];
        const searchArr = (tableFilters.search as string[]) || [];

        let sortBy = filters.sortBy;
        let sortDirection = filters.sortDirection;
        if (sorter && 'field' in sorter && sorter.field && sorter.order) {
            sortBy = sorter.columnKey as string;
            sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        let isActive: boolean | undefined = undefined;
        if (statusArr.length) {
            if (statusArr[0] === 'Active') isActive = true;
            if (statusArr[0] === 'Inactive') isActive = false;
        }

        setFilters({
            isActive,
            search: searchArr.length ? searchArr[0] : '',
            sortBy,
            sortDirection,
        });
    };
}

export const NoDataPlaceholder = ({message, styling = {}}: { message: string; styling?: React.CSSProperties }) => {
    const defaultStyle: React.CSSProperties = {
        height: '360px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        marginTop: '60px',
        marginBottom: '60px',
        ...styling
    };

    return (
        <div style={defaultStyle}>
            <Empty description={message}/>
        </div>
    );
};

export async function downscaleToJpegBase64(file: File, {maxW = 1200, maxH = 1200, quality = 0.7,}:
{ maxW?: number; maxH?: number; quality?: number } = {}): Promise<string> {
    const bitmap = await createImageBitmap(file);
    const ratio = Math.min(maxW / bitmap.width, maxH / bitmap.height, 1);
    const w = Math.round(bitmap.width * ratio);
    const h = Math.round(bitmap.height * ratio);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0, w, h);

    return canvas.toDataURL('image/jpeg', quality);
}

export function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

export const truncateStr = (s: string, n = 12) => (s && s.length > n ? `${s.slice(0, n)}…` : s);


export const useIsMobile = () => {
    const {useBreakpoint} = Grid;
    const screens = useBreakpoint();
    return !screens.md;
};


export const formatDateTime = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

export const detectPlatform = (url: string) => {
    try {
        const hostname = new URL(url).hostname.toLowerCase();

        if (hostname.includes('facebook.com')) return 'facebook';
        if (hostname.includes('instagram.com')) return 'instagram';
        if (hostname.includes('tiktok.com')) return 'tiktok';
        if (hostname.includes('twitter.com') || hostname.includes('x.com')) return 'x';
        if (hostname.includes('linkedin.com')) return 'linkedin';
        if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) return 'youtube';
        if (hostname.includes('twitch.tv')) return 'twitch';

        return null;
    } catch {
        return null;
    }
};

export const downloadFile = (url: string) => {
    try{
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    catch {}
};
